import { motion } from "framer-motion";
import { heritage } from "../data/site";

/**
 * "The Heritage" — the story of the parent family enterprise B. H. Rahaman Sheriff & Sons,
 * established 1924. Positioned between Legacy (BRA Sheriff developers) and Blueprint.
 */
export default function Heritage() {
    return (
        <section
            id="heritage"
            className="relative bg-[#111111] py-24 md:py-40 px-6 md:px-10 border-y border-[rgba(212,175,55,0.2)] overflow-hidden"
            data-testid="heritage-section"
        >
            {/* Giant background year */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden"
            >
                <span className="font-display italic text-[38vw] md:text-[24vw] leading-none text-[#C89B3C]/[0.045] select-none whitespace-nowrap tracking-tighter">
                    1924
                </span>
            </div>

            <div className="mx-auto max-w-[1440px] relative">
                <div className="flex items-center gap-6 mb-10">
                    <span className="w-16 h-px bg-[#C89B3C]" />
                    <span className="text-[10px] uppercase tracking-[0.4em] text-[#C89B3C]">
                        Chapter 16 — The Heritage
                    </span>
                </div>

                <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-16 md:mb-20">
                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{ duration: 0.9 }}
                        className="col-span-12 lg:col-span-8 font-display font-light text-white text-5xl md:text-6xl lg:text-7xl leading-[0.92] tracking-tight"
                    >
                        A century of the{" "}
                        <em className="text-[#E2C56D]">Sheriff</em>
                        <br />
                        family in <em className="text-[#E2C56D]">Bangalore.</em>
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{ duration: 0.9, delay: 0.15 }}
                        className="col-span-12 lg:col-span-4 text-[#bebebe] text-base leading-relaxed font-light"
                    >
                        Long before the buildings — there were the engines. The Sheriff family
                        has been shaping industry in Bangalore for over{" "}
                        <span className="text-[#E2C56D]">100 years.</span>
                    </motion.p>
                </div>

                {/* Origin card */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-15%" }}
                    transition={{ duration: 0.9 }}
                    className="grid grid-cols-12 gap-6 md:gap-10 mb-16 md:mb-20"
                >
                    <div className="col-span-12 md:col-span-5 border border-[rgba(212,175,55,0.25)] p-8 md:p-10 bg-[#0b0b0b]/70 backdrop-blur-xl">
                        <div className="text-[10px] uppercase tracking-[0.35em] text-[#C89B3C] mb-4">
                            The Origin
                        </div>
                        <div className="font-display text-6xl md:text-7xl text-[#E2C56D] leading-none">
                            1924
                        </div>
                        <div className="mt-6 font-display text-2xl md:text-3xl text-white leading-tight">
                            {heritage.parentCo}
                        </div>
                        <div className="mt-3 text-[#bebebe] text-sm font-light">
                            Speciality — Engine Rebuilding · Founded by{" "}
                            <span className="text-white">{heritage.founder}</span>
                        </div>
                        <div className="mt-8 pt-6 border-t border-[rgba(212,175,55,0.2)]">
                            <div className="text-[10px] uppercase tracking-[0.3em] text-[#7a7a7a] mb-2">
                                Head Office
                            </div>
                            <address className="not-italic text-white/90 text-sm font-light leading-relaxed">
                                {heritage.address}
                            </address>
                        </div>
                    </div>

                    {/* Award callout */}
                    <div className="col-span-12 md:col-span-7 relative overflow-hidden border border-[rgba(212,175,55,0.25)]">
                        <img
                            src="images/photo-realistic-golden-medal-with-winners-sash-symbolizing-accomplishment-honor-stock-photo-co.jpg"
                            alt="Vintage industrial machinery"
                            className="absolute inset-0 w-full h-full object-cover grayscale contrast-125 opacity-70"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-[#0b0b0b] via-[#0b0b0b]/60 to-[#0b0b0b]/20" />
                        <div className="relative z-10 p-8 md:p-10 h-full flex flex-col justify-between min-h-[320px]">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.35em] text-[#C89B3C]">
                                    A Legacy Achievement
                                </div>
                                <div className="mt-2 text-[10px] uppercase tracking-[0.35em] text-[#7a7a7a]">
                                    ◆ First in South India
                                </div>
                            </div>
                            <blockquote className="font-serif-alt text-white text-2xl md:text-3xl lg:text-4xl leading-[1.15] tracking-tight max-w-xl">
                                “The <em className="text-[#E2C56D] not-italic">first
                                lathe machine</em> in South India — built by our grandfather,
                                and preserved today at the{" "}
                                <span className="text-[#E2C56D]">Visvesvaraya Museum,
                                Bangalore.</span>”
                            </blockquote>
                            <div className="text-[10px] uppercase tracking-[0.35em] text-[#7a7a7a]">
                                <span className="text-[#C89B3C]">— Awarded ·</span>{" "}
                                Preserved · Displayed
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Three generations */}
                <div className="mt-8">
                    <div className="flex items-center gap-4 mb-10">
                        <span className="w-10 h-px bg-[#C89B3C]" />
                        <span className="text-[10px] uppercase tracking-[0.4em] text-[#C89B3C]">
                            Three Generations
                        </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 border-t border-[rgba(212,175,55,0.2)]">
                        {heritage.generations.map((g, i) => (
                            <motion.div
                                key={g.n}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-15%" }}
                                transition={{ duration: 0.8, delay: i * 0.12 }}
                                className={`relative p-8 md:p-10 border-b border-[rgba(212,175,55,0.2)] ${
                                    i !== heritage.generations.length - 1
                                        ? "md:border-r md:border-[rgba(212,175,55,0.2)]"
                                        : ""
                                } group`}
                                data-testid={`heritage-gen-${i}`}
                            >
                                <div className="flex items-baseline justify-between mb-6">
                                    <span className="font-display text-6xl md:text-7xl text-[#C89B3C]/30 group-hover:text-[#E2C56D]/70 transition-colors leading-none">
                                        {g.n}
                                    </span>
                                    <span className="text-[10px] uppercase tracking-[0.35em] text-[#C89B3C]">
                                        {g.year}
                                    </span>
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.35em] text-[#7a7a7a]">
                                    Generation
                                </div>
                                <div className="mt-2 font-display text-2xl md:text-3xl text-white leading-tight">
                                    {g.role}
                                </div>
                                <ul className="mt-6 space-y-2">
                                    {g.people.map((person) => (
                                        <li
                                            key={person}
                                            className="flex items-center gap-3 text-white/95 text-sm md:text-base font-light"
                                        >
                                            <span className="w-2 h-2 rotate-45 border border-[#C89B3C]" />
                                            {person}
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-6 text-[#bebebe] text-sm leading-relaxed font-light">
                                    {g.note}
                                </p>
                                <div className="mt-8 h-px w-8 bg-[#C89B3C] group-hover:w-24 transition-[width] duration-500" />
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
